import React, { useEffect, useState } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "src/components/common";
import { toast, ToastContainer } from "react-toastify";
import { ActionMenu } from "src/components/ui";
import { useLoading } from "src/hooks";
import { formatDateDmy } from "src/utils";
import CustomerTabs from "src/components/Requests/CustomerTab";
import { Input } from "src/components/inputs";
import searchIcon from "src/assets/images/input/searchIcon.svg";
import {
	useAppDispatch,
	useAppSelector,
	setUserRequests,
	setLoadingUserRequests,
	setUserRequestError,
	setSearchField,
	useLazyGetAllUserRequestsQuery,
	useValidateUserRequestMutation,
} from "src/store";
import type { RequestTypes as StoreRequestTypes } from "src/store/features/requestSlice";

interface Props {
	handleChange: (e: any) => void;
}

interface TransactionDetails {
	debited_for: string;
	account_number: string;
	status: string;
	bank_name: string;
	user_id: number;
}

interface RequestTypes {
	transaction_details: TransactionDetails;
	debited_for: string;
	amount: string;
	created_at: string;
	prev_balance: string;
	new_balance: string;
}

export const RhsHeading: React.FC<Props> = ({ handleChange }) => (
	<Input
		icon={<img src={searchIcon} alt='searchIcon' />}
		type='search'
		placeholder='Search'
		handleChange={handleChange}
	/>
);

const CustomerWithdrawalRequest = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	let [searchParams, setSearchParams] = useSearchParams();
	const [allowRowClick, setAllowRowClick] = useState(true);

	const { userRequests, loadingUserRequests, searchField } = useAppSelector(
		(state) => state.request
	);
	const [getAllUserRequests] = useLazyGetAllUserRequestsQuery();
	const [validateUserRequest] = useValidateUserRequestMutation();

	const handleChange = (e: any) => {
		dispatch(setSearchField(e.target.value));
	};

	useEffect(() => {
		document.title = "Request Page";
	}, []);

	useEffect(() => {
		const fetchUserRequests = async () => {
			dispatch(setLoadingUserRequests(true));
			dispatch(setUserRequestError(null));

			try {
				const result = await getAllUserRequests({page: 1, limit: 1}).unwrap();
				dispatch(
					setUserRequests(result.payload?.data?.pending_transaction || [])
				);
			} catch (error: any) {
				const errorMessage =
					error?.data?.error?.message || "Failed to fetch user requests";
				dispatch(setUserRequestError(errorMessage));
				console.error(error);
			} finally {
				dispatch(setLoadingUserRequests(false));
			}
		};

		fetchUserRequests();
	}, [dispatch, getAllUserRequests]);

	const filteredData = userRequests.filter((data) => {
		const account_name = data.debited_for ? data.debited_for.toLowerCase() : "";
		return account_name.includes(searchField.toLowerCase());
	});

	const handleNavigate = (row: { [x: string]: any }) => {
		navigate(`/customers/${row?.transaction_details?.user_id}`);
	};

	const handleValidation = async (row: any, value: string) => {
		try {
			const result = await validateUserRequest({
				transaction_id: row?.transaction_id,
				action: value,
			}).unwrap();
			toast.success(result?.message || "Request processed successfully");
		} catch (error: any) {
			const errorMessage =
				error?.data?.error?.message || "Failed to process request";
			toast.error(errorMessage);
		}
	};

	// Remove the old loading hook since we're using RTK loading state

	const RequestTableHeaders = [
		{
			title: "Account Name",
			render: (row: RequestTypes) => `${row.transaction_details.debited_for}`,
		},
		{
			title: "Account No",
			render: (row: RequestTypes) =>
				`${row.transaction_details.account_number}`,
		},
		{
			title: "Amount",
			render: (row: RequestTypes) => `${row.amount}`,
		},
		{
			title: "Date",
			render: (row: RequestTypes) => formatDateDmy(row.created_at),
		},
		{
			title: "Status",
			render: (row: RequestTypes) => (
				<p
					className={`capitalize ${
						row.transaction_details.status === "pending"
							? "text-[#55C4F1]"
							: "text-[#7607BD]"
					}`}
				>
					{row.transaction_details.status === "pending"
						? "active"
						: row.transaction_details.status}
				</p>
			),
		},
		{
			title: "Previous Balance",
			render: (row: RequestTypes) => `${row.prev_balance}`,
		},
		{
			title: "New Balance",
			render: (row: RequestTypes) => `${row.new_balance}`,
		},
		{
			title: "--",
			render: (row: UsersListTypes) => (
				<ActionMenu
					setAllowRowClick={(bool: boolean) => {
						setAllowRowClick(bool);
					}}
					actions={[
						{
							title: <p className=' text-blueX'>Approve</p>,
							onClick: () => {
								handleValidation(row, "approve");
							},
						},
						{
							title: <p className=' text-redX'>Reject</p>,
							onClick: () => {
								handleValidation(row, "reject");
							},
						},
					]}
				/>
			),
		},
	];
	console.log(filteredData);

	return (
		<div>
			<DashboardLayout
				pageTitle='Customer request withdrawal '
				rhsHeading={<RhsHeading handleChange={handleChange} />}
			>
				{/* <ToastContainer /> */}
				<div>
					{loadingUserRequests ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								marginTop: "100px",
							}}
						>
							<Loader>loading...</Loader>{" "}
						</div>
					) : filteredData.length > 0 ? (
						<CustomerTabs
							rows={userRequests}
							BookingsTableHeaders={RequestTableHeaders}
							allowRowClick={allowRowClick}
							title={
								<p className='count'>
									{filteredData.length > 1 ? filteredData.length : 0} Dispute
								</p>
							}
							onRowClick={handleNavigate}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
						/>
					) : (
						<p className='table-entry-status'>No Data Found</p>
					)}
				</div>
			</DashboardLayout>
		</div>
	);
};

export default CustomerWithdrawalRequest;
